import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import API from '../config';
import { Helmet } from 'react-helmet-async';
import OTPVerify from '../components/OTPVerify';

// ── Field wrapper ─────────────────────────────────────────────────────────────
const Field = ({ label, type = 'text', value, onChange, placeholder, required = true, children, T }) => (
  <div>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>{label}</label>
    {children || (
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required}
        style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', transition: 'border-color 0.15s', outline: 'none' }} />
    )}
  </div>
);

// ── Nickname Picker ───────────────────────────────────────────────────────────
const NicknamePicker = ({ token, onDone, T }) => {
  const [username, setUsername]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [checking, setChecking]   = useState(false);
  const [available, setAvailable] = useState(null);

  const randomSuggestion = () => {
    const adj  = ['sharp', 'bold', 'quick', 'smart', 'cool', 'top'];
    const noun = ['predictor', 'oracle', 'prophet', 'analyst', 'pundit', 'sage'];
    return `${adj[Math.floor(Math.random() * adj.length)]}_${noun[Math.floor(Math.random() * noun.length)]}${Math.floor(Math.random() * 999)}`;
  };
  const [suggestions] = useState(() => [randomSuggestion(), randomSuggestion(), randomSuggestion()]);

  const handleCheck = async (val) => {
    const cleaned = val.trim().toLowerCase().replace(/\s+/g, '_');
    setUsername(cleaned);
    setAvailable(null);
    if (cleaned.length < 3) return;
    setChecking(true);
    try {
      const res = await axios.post(`${API}/api/auth/check-username`, { username: cleaned });
      setAvailable(res.data.available);
    } catch (_) {}
    finally { setChecking(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || username.length < 3 || available !== true) return;
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/set-username`, { username }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onDone(username);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set username');
    } finally { setLoading(false); }
  };

  const borderColor = username.length >= 3
    ? available === true ? T.yes : available === false ? T.no : T.border
    : T.border;

  return (
    <div style={{ width: '100%', maxWidth: 420 }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎭</div>
        <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 8 }}>Pick your nickname</h1>
        <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6 }}>This is how other predictors will see you on Mino. Choose wisely!</p>
      </div>

      {error && <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 20, fontSize: 13 }}>{error}</div>}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Username</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMuted, fontSize: 14, pointerEvents: 'none' }}>@</span>
            <input type="text" value={username} onChange={e => handleCheck(e.target.value)}
              placeholder="your_nickname" required maxLength={30}
              style={{ width: '100%', background: T.surface, border: `1px solid ${borderColor}`, borderRadius: 8, padding: '12px 40px 12px 28px', color: T.text, fontSize: 14, fontFamily: 'inherit', transition: 'border-color 0.2s', outline: 'none' }} />
            <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12 }}>
              {checking ? <span style={{ color: T.textMuted }}>...</span>
                : username.length >= 3 && available === true  ? <span style={{ color: T.yes }}>✓</span>
                : username.length >= 3 && available === false ? <span style={{ color: T.no }}>✗</span>
                : null}
            </span>
          </div>
          <p style={{ color: T.textDim, fontSize: 11, marginTop: 6 }}>Letters, numbers, underscores only · 3–30 characters</p>
        </div>

        <div>
          <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Suggestions</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => handleCheck(s)}
                style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 10px', color: T.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: "'DM Mono', monospace", transition: 'all 0.1s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.text; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}>
                @{s}
              </button>
            ))}
          </div>
        </div>

        <button type="submit" disabled={loading || !username || username.length < 3 || available !== true}
          style={{ background: loading || !username || available === false ? T.surfaceHi : T.accent, color: loading || !username || available === false ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: loading || !username || available === false ? 'not-allowed' : 'pointer', fontSize: 14, transition: 'all 0.15s' }}>
          {loading ? 'Setting nickname...' : 'Continue →'}
        </button>
        <button type="button" onClick={() => onDone(null)}
          style={{ background: 'transparent', border: 'none', color: T.textDim, fontSize: 13, cursor: 'pointer', padding: 4 }}>
          Skip for now
        </button>
      </form>
    </div>
  );
};

// ── Main Register Component ───────────────────────────────────────────────────
export default function Register() {
  const [form, setForm]         = useState({ full_name: '', phone: '', email: '', password: '', confirmPassword: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [step, setStep]         = useState('register'); // register → verify → nickname
  const [appToken, setAppToken] = useState(null);
  const { login }               = useAuth();
  const { T }                   = useTheme();
  const navigate                = useNavigate();

  const set = field => e => setForm(p => ({ ...p, [field]: e.target.value }));

  // Step 1: validate + create account → backend sends OTP email
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 8)               { setError('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(form.password))           { setError('Password must contain at least one uppercase letter'); return; }
    if (!/[0-9]/.test(form.password))           { setError('Password must contain at least one number'); return; }

    const cleanedPhone = form.phone.replace(/\s+/g, '');
    const phoneRegex   = /^\+2547\d{8}$|^\+2541\d{8}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      setError('Enter a valid Kenyan phone number (e.g. +254712345678)');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/register`, {
        full_name: form.full_name,
        phone:     cleanedPhone,
        email:     form.email,
        password:  form.password,
      });
      setAppToken(res.data.token);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  // Step 2: OTP confirmed → get verified token, then go to nickname step
  const handleVerified = async (code) => {
    try {
      const res = await axios.post(`${API}/api/auth/verify-otp`, { otp: code }, {
        headers: { Authorization: `Bearer ${appToken}` },
      });
      login(res.data.user, res.data.token);
      setAppToken(res.data.token);
      setStep('nickname');
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Verification failed');
    }
  };

  // Resend OTP
  const handleResend = async () => {
    try {
      await axios.post(`${API}/api/auth/resend-otp`, {}, {
        headers: { Authorization: `Bearer ${appToken}` },
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.message || 'Failed to resend' };
    }
  };

  const handleNicknameDone = () => navigate('/dashboard');

  const stepLabel = { register: '1 of 3', verify: '2 of 3', nickname: '3 of 3' };

  const strength = (() => {
    const p = form.password;
    if (!p) return null;
    if (p.length < 6) return { label: 'Too short', color: T.no,   w: '25%' };
    if (p.length < 8) return { label: 'Weak',      color: T.no,   w: '40%' };
    if (!/[A-Z]/.test(p) || !/[0-9]/.test(p)) return { label: 'Fair', color: T.gold || '#f3a94a', w: '65%' };
    return { label: 'Strong', color: T.yes, w: '100%' };
  })();

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: T.text }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        input:focus { border-color:${T.accent} !important; outline:none; }
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <nav style={{ height: 56, display: 'flex', alignItems: 'center', padding: '0 24px', borderBottom: `1px solid ${T.border}` }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <svg width="140" height="38" viewBox="0 0 160 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#131416" stroke="#2a2b2f" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="3" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="3" strokeLinecap="round"/>
            <text x="46" y="20" dominantBaseline="middle" fontFamily="'DM Sans',sans-serif" fontWeight="800" fontSize="18" fill={T.text} letterSpacing="-1">Mino Markets</text>
          </svg>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: T.textDim, fontFamily: "'DM Mono', monospace" }}>
          Step {stepLabel[step]}
        </span>
      </nav>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px' }}>

        {step === 'verify' && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            <OTPVerify email={form.email} onDone={handleVerified} onResend={handleResend} T={T} />
          </div>
        )}

        {step === 'nickname' && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            <NicknamePicker token={appToken} onDone={handleNicknameDone} T={T} />
          </div>
        )}

        {step === 'register' && (
          <div style={{ width: '100%', maxWidth: 420, animation: 'fadeIn 0.3s ease' }}>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 6 }}>Create account</h1>
              <p style={{ color: T.textMuted, fontSize: 14 }}>Start predicting on Mino</p>
            </div>

            {error && (
              <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 20, fontSize: 13 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Full name" value={form.full_name} onChange={set('full_name')} placeholder="John Doe" T={T} />
                <Field label="Phone" type="tel" value={form.phone} onChange={set('phone')} placeholder="+254 7XX XXX XXX" T={T} />
              </div>

              <Field label="Email" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" T={T} />

              <Field label="Password" T={T}>
                <div style={{ position: 'relative' }}>
                  <input type={showPass ? 'text' : 'password'} value={form.password} onChange={set('password')} required placeholder="Min. 8 characters"
                    style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 42px 12px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.15s' }} />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                    {showPass ? 'hide' : 'show'}
                  </button>
                </div>
                {strength && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ height: 3, background: T.surfaceHi, borderRadius: 2, overflow: 'hidden', marginBottom: 5 }}>
                      <div style={{ height: '100%', width: strength.w, background: strength.color, borderRadius: 2, transition: 'all 0.3s ease' }} />
                    </div>
                    <span style={{ fontSize: 11, color: strength.color, fontFamily: "'DM Mono', monospace" }}>{strength.label}</span>
                  </div>
                )}
              </Field>

              <Field label="Confirm password" T={T}>
                <input type={showPass ? 'text' : 'password'} value={form.confirmPassword} onChange={set('confirmPassword')} required placeholder="Repeat password"
                  style={{ width: '100%', background: T.surface, border: `1px solid ${form.confirmPassword && form.confirmPassword !== form.password ? T.no : T.border}`, borderRadius: 8, padding: '12px 14px', color: T.text, fontSize: 14, fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.15s' }} />
              </Field>

              <button type="submit" disabled={loading}
                style={{ background: loading ? T.surfaceHi : T.accent, color: loading ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 14, marginTop: 4, transition: 'all 0.15s' }}>
                {loading ? 'Creating account...' : 'Create account →'}
              </button>
            </form>

            {/* After </form> in the login step */}

<div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
  <div style={{ flex: 1, height: 1, background: T.border }} />
  <span style={{ color: T.textDim, fontSize: 12 }}>or</span>
  <div style={{ flex: 1, height: 1, background: T.border }} />
</div>

<button
  type="button"
  onClick={() => window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`}
  style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 13, fontWeight: 600, cursor: 'pointer', fontSize: 14, color: T.text, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'border-color 0.15s' }}
  onMouseEnter={e => e.currentTarget.style.borderColor = T.accent}
  onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
>
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.6 26.8 36 24 36c-5.2 0-9.6-2.9-11.3-7.1l-6.6 4.9C9.6 39.6 16.3 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.4-2.5 4.4-4.6 5.8l6.2 5.2C40.8 35.5 44 30.2 44 24c0-1.3-.1-2.7-.4-4z"/>
  </svg>
  Continue with Google
</button>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <p style={{ color: T.textMuted, fontSize: 13 }}>
                Already have an account?{' '}
                <span onClick={() => navigate('/login')} style={{ color: T.accent, cursor: 'pointer', fontWeight: 600 }}>Sign in</span>
              </p>
            </div>

            <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${T.border}`, textAlign: 'center' }}>
              <p style={{ color: T.textDim, fontSize: 11, lineHeight: 1.6 }}>
                By creating an account, you agree to Mino's{' '}
                <span onClick={() => navigate('/terms')} style={{ color: T.textMuted, cursor: 'pointer' }}>Terms of Use</span>
                {' '}and confirm you are 18 years or older.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}